import { ReactElement, useContext, useMemo, useState } from 'react';

import { Button, Flex } from '@chakra-ui/react';
import { Form, Formik } from 'formik';

import { DealStateEnum } from '../../gql/dealGql';
import { FinancialInfo } from '../../gql/financialInfoGql';
import {
  DealProduct,
  DealType,
  Maybe,
  PaperworkType,
  ProductSlug,
  useOnCreditDecisionSelectedSubscription,
} from '../../gql/generated/graphql';

import ConfirmEditModal from '../ConfirmEditModal/ConfirmEditModal';
import GenerateDocumentsButton from '../GenerateDocs/GenerateDocumentsButton';
import SendToNetsuiteButton from '../SendNetsuite/SendToNetsuiteButton';
import { TitleWorkButtonWithContext } from '../forwardButtons/TitleWorkButton';
import Card from '../shared/Card';
import Tooltip from '../shared/Tooltip';
import AutoStructureButton from './components/buttons/AutoStructureButton';
import CloseDealButton from './components/buttons/CloseDealButton';
import CreateBookSheetButton from './components/buttons/CreateBookSheetButton';
import SaveButton from './components/buttons/SaveButton';
import SendPayoffToFirstFedButton from './components/buttons/SendPayoffToFirstFedButton';
import SendToCloser from './components/buttons/SendToCloserButton';
import SubmitCreditAppButton from './components/buttons/SubmitCreditAppButton';
import UploadDocumentsToCUDLButton from './components/buttons/UploadDocumentsToCUDLButton';

import { DealInfoBuyoutForm } from './DealInfoBuyoutForm';
import { validationSchema } from './validationSchema';

import { isDealInOrPastFunded, isUneditable } from '../../constants/dealStates';
import { PermissionEnum } from '../../constants/permissions';
import { useBanks } from '../../hooks/useBanks';
import { DealActionsEnum, DealContext } from '../../libs/DealContext';
import { AbilityContext, ModalContext } from '../../libs/contextLib';
import { passValuesToSchema } from '../../libs/utils';
import DisabledLienholderModal from '../../pages/DealDetail/DisabledLienholderModal';
import MarkAsSigned from '../../pages/DealDetail/ProgressionButtons/MarkAsSigned';
import MoveToButton from '../../pages/DealDetail/ProgressionButtons/MoveToButton';
import MoveToFundedOrReadyForPickup from '../../pages/DealDetail/ProgressionButtons/MoveToFundedOrReadyForPickup';
import SendToSignaturesButton from '../../pages/DealDetail/ProgressionButtons/SendToSignaturesButton';
import { DealDates } from '../../types/dealDates';

const DealInfoBuyout = () => {
  const { deal, dispatch, claimIsRequired } = useContext(DealContext);
  const { modalCloseCount } = useContext(ModalContext);
  const abilities = useContext(AbilityContext);

  const { banks } = useBanks({ onlyActive: true, deal });

  const [isConfirmEditModalOpen, setIsConfirmEditModalOpen] = useState(false);
  const [isEditingWithPermission, setIsEditingWithPermission] = useState(false);

  const isRefi = deal.type === DealType.Refi;
  const inClosed = deal.state === DealStateEnum.Closed;
  const inClosing = deal.state === DealStateEnum.Closing;
  const inStructuring = deal.state === DealStateEnum.Structuring;
  const canEditFundedOnwards =
    abilities.has(PermissionEnum.EditFundedOnwards) && isDealInOrPastFunded(deal.state);

  const forwardButton = useMemo(() => {
    const isPaperSign = deal.paperwork_type === PaperworkType.Paper;

    const buttonsByState: Partial<Record<DealStateEnum, ReactElement | null>> = {
      [DealStateEnum.Closed]: isPaperSign ? <SendToSignaturesButton /> : null,
      [DealStateEnum.SentForSignatures]: isPaperSign ? <MarkAsSigned /> : null,
      [DealStateEnum.Signed]: <MoveToFundedOrReadyForPickup />,
      [DealStateEnum.ReadyForPickup]: <MoveToButton newState={DealStateEnum.AtAuction} />,
      [DealStateEnum.PaidOff]: <MoveToButton newState={DealStateEnum.ReadyForPickup} />,
      [DealStateEnum.AtAuction]: <MoveToButton newState={DealStateEnum.Sold} />,
      [DealStateEnum.Sold]: <MoveToButton newState={DealStateEnum.TitleSent} />,
      [DealStateEnum.TitleSent]: <MoveToButton newState={DealStateEnum.Finalized} />,
      [DealStateEnum.Funded]: <TitleWorkButtonWithContext />,
      [DealStateEnum.SendPayoff]: <TitleWorkButtonWithContext />,
      [DealStateEnum.WaitingForTitle]: <TitleWorkButtonWithContext />,
      [DealStateEnum.TitleReceived]: <TitleWorkButtonWithContext />,
      [DealStateEnum.SentToProcessor]: <TitleWorkButtonWithContext />,
    };

    const dealStateButton = buttonsByState[deal.state];
    if (!dealStateButton) {
      return null;
    }

    if (claimIsRequired) {
      return <Tooltip errors={['Deal must be claimed']}>{dealStateButton}</Tooltip>;
    }
    return dealStateButton;
  }, [deal.paperwork_type, deal.state, claimIsRequired]);

  useOnCreditDecisionSelectedSubscription({
    skip: !deal.id,
    variables: {
      dealId: deal.id ?? -1,
    },
    onData: ({ data }) => {
      if (!data.data?.onCreditDecisionSelected) {
        return;
      }

      const updatedFinancialInfo = data.data.onCreditDecisionSelected.financialInfo;

      // This will trigger the recalculation of the payment options.
      dispatch({
        type: DealActionsEnum.DeepUpdateDeal,
        payload: {
          financial_info: {
            bank: updatedFinancialInfo.bank ?? undefined,
            bank_fees: updatedFinancialInfo.bank_fees as number,
            selected_credit_decision_id: updatedFinancialInfo.selected_credit_decision_id as number,
            term: updatedFinancialInfo.term as number,
            sell_rate: updatedFinancialInfo.sell_rate as number,
            buy_rate: updatedFinancialInfo.buy_rate as number,
            first_payment_date: updatedFinancialInfo.first_payment_date,
          },
        },
      });
    },
  });

  // Derive T&W/Key prices from deal.deal_products for initialValues.
  // These fields aren't native Deal GQL fields, so without this they get wiped by enableReinitialize.
  const twProduct = deal.deal_products?.find(
    (dp: Maybe<DealProduct>) => dp?.product_catalog?.slug === ProductSlug.TireWheel,
  );
  const krProduct = deal.deal_products?.find(
    (dp: Maybe<DealProduct>) => dp?.product_catalog?.slug === ProductSlug.KeyReplacement,
  );
  // VSC/GAP also live on deal_products now. Their financial_info.* form fields
  // are kept (approach A) but sourced from deal_products so the form is backed by
  // the authoritative table; the legacy financial_info columns are only a fallback
  // until they're removed.
  const vscProduct = deal.deal_products?.find(
    (dp: Maybe<DealProduct>) => dp?.product_catalog?.slug === ProductSlug.Vsc,
  );
  const gapProduct = deal.deal_products?.find(
    (dp: Maybe<DealProduct>) => dp?.product_catalog?.slug === ProductSlug.Gap,
  );

  const initialValues = useMemo(
    () => ({
      ...deal,
      tire_wheel_price: twProduct?.price ?? null,
      tire_wheel_cost: twProduct?.cost ?? null,
      key_replacement_price: krProduct?.price ?? null,
      key_replacement_cost: krProduct?.cost ?? null,
      // `deal_dates` needs to be initialized so that the initial date values are `undefined` instead of `null`.
      // This prevents the form from incorrectly detecting the values as changed when creating the `custom_dates` object.
      deal_dates: deal.deal_dates ?? new DealDates(),
      financial_info: deal.financial_info
        ? {
            ...deal.financial_info,
            // VSC/GAP product fields come from deal_products (authoritative).
            // The legacy financial_info columns are no longer fetched (Step 4),
            // so there is no fallback — these Formik fields are deal_products-backed.
            vsc_price: vscProduct?.price ?? null,
            vsc_cost: vscProduct?.cost ?? null,
            vsc_term: vscProduct?.term ?? null,
            vsc_type: vscProduct?.coverage_type ?? null,
            pen_vsc_rate_id: vscProduct?.pen_rate_id ?? null,
            pen_vsc_session_id: vscProduct?.pen_session_id ?? null,
            pen_vsc_form_id: vscProduct?.pen_form_id ?? null,
            gap_price: gapProduct?.price ?? null,
            gap_cost: gapProduct?.cost ?? null,
            pen_gap_rate_id: gapProduct?.pen_rate_id ?? null,
            pen_gap_session_id: gapProduct?.pen_session_id ?? null,
            pen_gap_form_id: gapProduct?.pen_form_id ?? null,
          }
        : new FinancialInfo(deal.id),
    }),
    [
      modalCloseCount,
      deal.state,
      deal.financial_info?.selected_credit_decision_id,
      twProduct?.price,
      twProduct?.cost,
      krProduct?.price,
      krProduct?.cost,
      vscProduct?.price,
      vscProduct?.cost,
      vscProduct?.term,
      vscProduct?.coverage_type,
      vscProduct?.pen_rate_id,
      vscProduct?.pen_session_id,
      vscProduct?.pen_form_id,
      gapProduct?.price,
      gapProduct?.cost,
      gapProduct?.pen_rate_id,
      gapProduct?.pen_session_id,
      gapProduct?.pen_form_id,
    ],
  );

  return (
    <Card variant="rounded">
      <Formik
        validate={(values) =>
          passValuesToSchema(values, validationSchema, { ...values, deal, abilities, banks })
        }
        onSubmit={() => undefined}
        initialValues={initialValues}
        validateOnMount
        enableReinitialize
      >
        <Form noValidate>
          <DealInfoBuyoutForm
            banks={banks}
            isEditingWithPermission={isEditingWithPermission}
            setIsEditingWithPermission={setIsEditingWithPermission}
            isEditable={!isUneditable(deal.state) || isEditingWithPermission}
          />
          <Flex pt={4} px={4} justifyContent="end" flexWrap="wrap" gap={2}>
            {/* secondary buttons */}
            {canEditFundedOnwards ? (
              isEditingWithPermission && (
                <SaveButton customOnClick={() => setIsEditingWithPermission(false)} />
              )
            ) : (
              <SaveButton />
            )}
            {canEditFundedOnwards && (
              <>
                <Button
                  variant="secondary"
                  hidden={isEditingWithPermission}
                  onClick={() => {
                    setIsConfirmEditModalOpen(true);
                  }}
                >
                  Edit
                </Button>
                <ConfirmEditModal
                  isOpen={isConfirmEditModalOpen}
                  onConfirm={() => {
                    setIsConfirmEditModalOpen(false);
                    setIsEditingWithPermission(true);
                  }}
                  onClose={() => {
                    setIsConfirmEditModalOpen(false);
                  }}
                />
              </>
            )}
            {(inStructuring || inClosing) && <SubmitCreditAppButton banks={banks} />}
            <CreateBookSheetButton />
            <UploadDocumentsToCUDLButton />

            {/* primary buttons */}
            {inStructuring && !isRefi && abilities.has(PermissionEnum.AutomateCreditApplication) ? (
              <AutoStructureButton />
            ) : null}
            <SendToCloser />
            {inClosing && <CloseDealButton />}
            {inClosed && deal.paperwork_type === PaperworkType.Paper && <GenerateDocumentsButton />}
            <SendToNetsuiteButton />
            <SendPayoffToFirstFedButton />
            {forwardButton}

            <DisabledLienholderModal />
          </Flex>
        </Form>
      </Formik>
    </Card>
  );
};

export default DealInfoBuyout;
